import {useEffect, useRef, useState} from 'react';
import {EmitterSubscription, Keyboard, LayoutAnimation} from 'react-native';
import {partial} from 'lodash';
import {constant, flow} from 'lodash/fp';

import platformConfig from '/config/platform.config';
import {enableLayoutAnimation} from '/util/animation.util';

export function useAnimationConfigurator(): () => void {
  useEffect(() => {
    enableLayoutAnimation();
  }, []);

  const configuratorHandler = useRef(() => {
    LayoutAnimation.configureNext(
      LayoutAnimation.create(
        200,
        LayoutAnimation.Types.easeOut,
        LayoutAnimation.Properties.opacity,
      ),
    );
  });

  return configuratorHandler.current;
}

interface UseKeyboardStateOptions {
  animationEffect?: () => void;
}

interface KeyboardState {
  isKeyboardShown: boolean;
}

export function useKeyboardState({
  animationEffect = constant(null),
}: UseKeyboardStateOptions = {}): KeyboardState {
  const [isKeyboardShown, setKeyboard] = useState<boolean>(false);

  useEffect(() => {
    const showKeyboard = flow(animationEffect, partial(setKeyboard, true));
    const hideKeyboard = flow(animationEffect, partial(setKeyboard, false));

    let subscriptions: EmitterSubscription[] = [];
    if (platformConfig.getOS() === 'ios') {
      subscriptions = [
        Keyboard.addListener('keyboardWillShow', showKeyboard),
        Keyboard.addListener('keyboardWillHide', hideKeyboard),
      ];
    } else {
      subscriptions = [
        Keyboard.addListener('keyboardDidShow', showKeyboard),
        Keyboard.addListener('keyboardDidHide', hideKeyboard),
      ];
    }

    return () => {
      subscriptions.forEach(subscription => subscription?.remove());
    };
  }, [animationEffect]);

  return {isKeyboardShown};
}
